import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import ChatList from './ChatList';
import ChatInterface from './ChatInterface';
import '../styles/ChatDrawer.css';

const ChatDrawer = () => {
  const { user } = useAuthState();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null); // { type: 'event' | 'group', id: string }
  const [returnTo, setReturnTo] = useState(null); // Path to return to when closing chat
  const drawerRef = useRef(null);

  const handleClose = () => {
    setIsOpen(false);
    if (returnTo) {
      // Navigate back to the page that opened the chat
      navigate(returnTo);
      setReturnTo(null);
    }
    setSelectedChat(null);
    // Refresh chat list when returning (to update unread counts)
    window.dispatchEvent(new Event('chatListRefresh'));
  };
  const startXRef = useRef(null);
  const currentXRef = useRef(null);
  const isDraggingRef = useRef(false);

  // Handle swipe gestures
  useEffect(() => {
    if (!user) return;

    const handleTouchStart = (e) => {
      // Only start swipe from left edge (first 20px)
      if (e.touches[0].clientX > 20) return;
      
      startXRef.current = e.touches[0].clientX;
      isDraggingRef.current = true;
    };

    const handleTouchMove = (e) => {
      if (!isDraggingRef.current) return;
      
      currentXRef.current = e.touches[0].clientX;
      const diff = currentXRef.current - startXRef.current;
      
      // Only allow opening swipe (right direction)
      if (diff > 0 && !isOpen) {
        const drawer = drawerRef.current;
        if (drawer) {
          const maxWidth = 400; // Max drawer width
          const translateX = Math.min(diff, maxWidth);
          drawer.style.transform = `translateX(${translateX - maxWidth}px)`;
        }
      }
    };

    const handleTouchEnd = () => {
      if (!isDraggingRef.current) return;
      
      const diff = currentXRef.current - startXRef.current;
      
      // If swiped more than 100px, open drawer
      if (diff > 100 && !isOpen) {
        setIsOpen(true);
      }
      
      // Reset transform
      if (drawerRef.current) {
        drawerRef.current.style.transform = '';
      }
      
      isDraggingRef.current = false;
      startXRef.current = null;
      currentXRef.current = null;
    };

    // Add touch listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [user, isOpen]);

  // Close drawer when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isOpen && drawerRef.current && !drawerRef.current.contains(e.target)) {
        // Check if click is on the backdrop
        if (e.target.classList.contains('chat-drawer-backdrop')) {
          setIsOpen(false);
          setSelectedChat(null);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Keyboard shortcut: Cmd/Ctrl + K to toggle
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(!isOpen);
      }
      
      // ESC to close
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Listen for openChat custom event (to programmatically open a specific chat)
  useEffect(() => {
    const handleOpenChat = (event) => {
      const { type, id, returnTo: returnPath } = event.detail;
      if (type && id) {
        setIsOpen(true);
        setSelectedChat({ type, id });
        setReturnTo(returnPath || null);
      }
    };

    window.addEventListener('openChat', handleOpenChat);
    return () => {
      window.removeEventListener('openChat', handleOpenChat);
    };
  }, []);

  if (!user) return null;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div 
          className="chat-drawer-backdrop"
          onClick={handleClose}
        />
      )}

      {/* Drawer */}
      <div 
        ref={drawerRef}
        className={`chat-drawer ${isOpen ? 'open' : ''}`}
      >
        {!selectedChat && (
          <div className="chat-drawer-header">
            <h2>Chats</h2>
            <button
              className="chat-drawer-close"
              onClick={() => {
                handleClose();
              }}
              title="Close (ESC)"
            >
              ✕
            </button>
          </div>
        )}

        <div className="chat-drawer-content">
          {selectedChat ? (
            <ChatInterface
              chatType={selectedChat.type}
              chatId={selectedChat.id}
              onBack={() => {
                handleClose();
              }}
            />
          ) : (
            <ChatList
              onSelectChat={(type, id) => {
                setSelectedChat({ type, id });
                setReturnTo(null); // Clear return path when selecting from list
              }}
            />
          )}
        </div>
      </div>

      {/* Toggle button - floating button on left edge */}
      {!isOpen && (
        <button
          className="chat-drawer-toggle"
          onClick={() => setIsOpen(true)}
          title="Open Chats (Cmd/Ctrl + K)"
        >
          💬
        </button>
      )}
    </>
  );
};

export default ChatDrawer;

